import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { Action, BulkActionsBar } from 'ui/bulkActionsBar';
import { Analytics16, Archive16, Delete16, Export16, FolderMove16 } from 'ui/icons';
import { FolderBreadcrumbs } from 'ui/indexPages/folders/folderBreadcrumbs';
import { PAGE_SIZES, PaginationFooter, PaginationUpdateEvent } from 'ui/paginationFooter';
import { fonts } from 'ui/theme';
import { FullWidth } from 'ui/utility/fullWidth';
import { usePaginationContext } from 'utils/contexts/pagination';
import { isSelectionContent, isSelectionFolder } from 'utils/contexts/selection';
import { useClearSearchParam } from 'utils/hooks/useClearSearchParam';
import { useClearSearchParamOnValue } from 'utils/hooks/useClearSearchParamOnValue';
import { getStartTime } from 'utils/indexPageUtils';

import { CampaignIndexHeader } from '../CampaignIndexHeader';
import { getCampaignMetricsRequest } from '../actions';
import {
  CAMPAIGN_STATE_FILTERS,
  FOLDER_ID_SEARCH_PARAM,
  PARENT_CAMPAIGN_ID_SEARCH_PARAM,
  UNSET_PARENT_CAMPAIGN_ID,
} from '../constants';
import { CampaignId, GraphQLCampaign } from '../domainTypes';
import { useExportCampaignsAsCSV } from '../hooks';
import { useCampaignFolderModals } from '../hooks/useCampaignFolderModals';
import { useHasFilters } from '../hooks/useHasFilters';
import { selectCampaignMetrics } from '../selectors';
import { CampaignFolderModals } from './CampaignFolderModals';
import { CampaignFoldersTable } from './CampaignFoldersTable';
import { CampaignTableControls } from './CampaignTableControls';
import { RecurringCampaignBreadcrumbs } from './RecurringCampaignBreadcrumbs';
import { fetchRootFolderIdResponse } from './campaignFolderActions';
import { useCampaignFiltersContext } from './contexts/CampaignFiltersContext';
import { useCampaignQueryContext } from './contexts/CampaignQueryContext';
import { useSelectionContext } from './contexts/SelectionContext';

// This is a temporary work-around until angular can be removed (which adds Proxima Nova to the body)
const CampaignFoldersContainer = styled.div`
  font-family: ${fonts.sansSerif};
`;

const displayContent: { [key: string]: string } = {
  // aggregateMetrics: 'View metrics for { campaignCount } campaigns',
  archive: 'Archive',
  delete: 'Delete',
  exportCSV: 'Export to CSV',
  moveToFolder: 'Move to folder',
  unarchive: 'Unarchive',
};

/** Parses selected filters for graphql request */

export const CampaignFoldersPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { setCurrentPage, setPageSize } = usePaginationContext();
  const { parentCampaignId, stateCategory } = useCampaignFiltersContext();
  const {
    campaigns: childCampaigns,
    folderId: currentFolderId,
    folders: childFolders,
    folderData,
    groupRecurringCampaigns,
    hasResponseError,
    paginationInfo,
    parentCampaign,
    rootFolderId,
  } = useCampaignQueryContext();
  const { bulk, clearAll, selectBulk } = useSelectionContext();

  const modals = useCampaignFolderModals();
  const canClearFilters = useHasFilters();

  const isViewingRecurringChildren = groupRecurringCampaigns && Boolean(parentCampaignId);

  // Effects

  useClearSearchParam(FOLDER_ID_SEARCH_PARAM, hasResponseError);
  useClearSearchParamOnValue(PARENT_CAMPAIGN_ID_SEARCH_PARAM, `${UNSET_PARENT_CAMPAIGN_ID}`);

  /** close all the modals when the page navigates away */
  useEffect(() => {
    return () => {
      modals.all.toggle();
    };
  }, []);

  // Effect to deselect all each time the user navigates between folders
  useEffect(() => {
    if (folderData.id !== currentFolderId) {
      clearAll();
    }
  }, [currentFolderId]);

  // Get the Delivery column metrics for the currently displayed campaigns
  const campaignIds: CampaignId[] = useMemo(
    () => childCampaigns.map((campaign: GraphQLCampaign) => campaign.id),
    [childCampaigns],
  );
  const campaignMetrics = useSelector(selectCampaignMetrics);

  useEffect(() => {
    if (campaignIds.length > 0) {
      // Only fetch campaign ids if metrics are not already stored
      const campaignsToFetch = campaignIds.filter((id) => !(id in campaignMetrics));
      if (campaignsToFetch.length > 0) {
        dispatch(getCampaignMetricsRequest(campaignsToFetch));
      }
    }
  }, [campaignIds]);

  useEffect(() => {
    dispatch(fetchRootFolderIdResponse(rootFolderId));
  }, [dispatch, rootFolderId]);

  // Derived data and callbacks

  const handlePaginationUpdate = ({ page, pageSize }: PaginationUpdateEvent): void => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const selectedCampaigns = bulk.filter(isSelectionContent);
  const selectedFolders = bulk.filter(isSelectionFolder);

  const bulkActions: Action[] = [
    {
      active: stateCategory === CAMPAIGN_STATE_FILTERS[4].id,
      label: displayContent.unarchive,
      onClick: () => {
        selectBulk();
        modals.unarchive.toggle(true);
      },
      sortIndex: 1,
    },
    {
      active:
        stateCategory !== CAMPAIGN_STATE_FILTERS[4].id &&
        selectedCampaigns.length > 0 &&
        selectedFolders.length === 0,
      icon: <Archive16 />,
      label: displayContent.archive,
      onClick: () => {
        selectBulk();
        modals.archive.toggle(true);
      },
      sortIndex: 1,
    },
    {
      active: selectedCampaigns.length > 0 && selectedFolders.length === 0,
      icon: <Export16 />,
      label: displayContent.exportCSV,
      onClick: useExportCampaignsAsCSV(
        selectedCampaigns.map((selectedCampaign) => selectedCampaign.id),
      ),
      sortIndex: 2,
    },
    {
      active: true,
      icon: <FolderMove16 />,
      label: displayContent.moveToFolder,
      onClick: () => {
        selectBulk();
        modals.move.toggle(true);
      },
      sortIndex: 3,
    },
    {
      active: selectedFolders.length > 0,
      icon: <Delete16 />,
      label: displayContent.delete,
      onClick: () => {
        selectBulk();
        modals.delete.toggle(true);
      },
      sortIndex: 4,
    },
    {
      active: selectedCampaigns.length > 0 && selectedFolders.length === 0, // TBD
      icon: <Analytics16 />,
      label: `View metrics for ${selectedCampaigns.length} ${
        selectedCampaigns.length > 1 ? 'campaigns' : 'campaign'
      }`,
      onClick: () => {
        if (selectedCampaigns.length === 1) {
          history.push(`/analytics/campaignPerformance?campaignId=${selectedCampaigns[0].id}`);
        } else {
          const selectedIds = selectedCampaigns.map((selection) => selection.id);
          const campaigns = selectedCampaigns.map((campaign) => campaign.node);
          // @ts-ignore
          const startTime = getStartTime(campaigns);
          const endTime = new Date().toISOString();
          // @ts-ignore
          const mediums = new Set(campaigns.map((campaign) => campaign.messageMedium));
          history.push(
            `/messagingInsights?campaignIds=${selectedIds.join(',')}&messageMediums=${[
              ...mediums,
            ].join(',')}&startTime=${startTime}&endTime=${endTime}`,
          );
        }
      },
      sortIndex: 5,
    },
  ].filter((action) => action.active);

  const showBulkActionBar = bulk.length > 0;
  // Show pagination only when table has items or is dislaying no search results
  const showPagination = childCampaigns.length > 0 || childFolders.length > 0 || canClearFilters;

  let breadcrumbs = null;
  if (isViewingRecurringChildren) {
    breadcrumbs = (
      <RecurringCampaignBreadcrumbs parentCampaign={parentCampaign?.name ?? 'Parent Campaign'} />
    );
  } else if (folderData.ancestors && folderData.id !== rootFolderId) {
    breadcrumbs = (
      <FolderBreadcrumbs
        folderAncestors={[...folderData.ancestors, { id: folderData.id, name: folderData.name }]}
        rootFolderId={rootFolderId}
      />
    );
  }

  return (
    <CampaignFoldersContainer data-test="campaign-folders-page">
      <CampaignIndexHeader currentFolderId={currentFolderId} />
      {!showBulkActionBar && <CampaignTableControls />}
      <FullWidth>
        {showBulkActionBar && <BulkActionsBar actions={bulkActions} numSelected={bulk.length} />}
        {breadcrumbs}
        <CampaignFoldersTable />
        {showPagination && (
          <PaginationFooter
            currentPage={paginationInfo.page}
            itemsPerPage={paginationInfo.limit}
            numItems={paginationInfo.count}
            pageSizes={PAGE_SIZES}
            onUpdate={handlePaginationUpdate}
          />
        )}
      </FullWidth>
      <CampaignFolderModals />
    </CampaignFoldersContainer>
  );
};
